import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, ViewStyle } from 'react-native';

import styles from './option-choose-container.style';
import { useErrorSound } from '../../hooks/use-error-sound';
import { useSuccessSound } from '../../hooks/use-success-sound';
import { CheckButton } from '../check-button/check-button';
import { ChoiceBubble } from '../choice-bubble/choice-bubble';
import { ChoiceBubbleStatus } from '../choice-bubble/choice-bubble-status.enum';

interface OptionChooseContainerProps {
  checkButtonActive?: boolean;
  containerStyle?: ViewStyle;
  answer: string;
  options;
  audioMode?: boolean;
  isImage?: boolean;
  onAnswer?: (correct: boolean) => void;
  scrollToEnd?: () => void;
  index?: number;
  disabled?: boolean;
}

export function OptionChooseContainer({
  disabled = false,
  checkButtonActive: hasCheckEnabled = true,
  answer,
  containerStyle,
  options,
  audioMode = false,
  isImage = false,
  onAnswer,
  index,
  scrollToEnd,
}: OptionChooseContainerProps) {
  const correctSound = useSuccessSound();
  const incorrectSound = useErrorSound();

  const [showSelection, setShowSelection] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number>();

  useEffect(() => {
    setSelectedIndex(undefined);
    setShowSelection(false);
  }, [index, options]);

  const isCorrect = useMemo(() => {
    if (!options || selectedIndex === undefined) {
      return false;
    }

    return options[selectedIndex] === answer;
  }, [answer, options, selectedIndex]);

  const playSound = useCallback(
    (isCorrect) => {
      if (isCorrect) {
        correctSound.play();
      } else {
        incorrectSound.play();
      }
    },
    [correctSound, incorrectSound]
  );

  const verifyAnswer = useCallback(() => {
    if (!onAnswer || disabled || !playSound) {
      return;
    }

    playSound(isCorrect);
    setShowSelection(true);
    onAnswer(isCorrect);
    if (scrollToEnd) {
      scrollToEnd();
    }
  }, [onAnswer, disabled, isCorrect, playSound, scrollToEnd]);

  useEffect(() => {
    if (selectedIndex === undefined) {
      return;
    }

    if (scrollToEnd) {
      scrollToEnd();
    }
  }, [selectedIndex, scrollToEnd]);

  useEffect(() => {
    if (hasCheckEnabled || selectedIndex === undefined || showSelection) {
      return;
    }

    verifyAnswer();
  }, [hasCheckEnabled, selectedIndex, verifyAnswer, showSelection]);

  const showCheck =
    hasCheckEnabled && !showSelection && selectedIndex !== undefined;

  const buttons = options.map((option: string, i: number) => {
    let status = ChoiceBubbleStatus.DEFAULT;

    if (selectedIndex === i && showSelection) {
      status =
        options[i] === answer
          ? ChoiceBubbleStatus.CORRECT
          : ChoiceBubbleStatus.INCORRECT;
    } else if (selectedIndex === i && !showSelection) {
      status = ChoiceBubbleStatus.SELECTED;
    }

    if (audioMode) {
      return (
        <ChoiceBubble
          key={`button-${i}`}
          text={`Audio ${i + 1}`}
          audioUrl={option}
          status={status}
          onPress={() => setSelectedIndex(i)}
          disabled={disabled}
          optionKey={`${i + 1}`}
          buttonStyle={styles.button}
        />
      );
    } else if (isImage) {
      return (
        <ChoiceBubble
          key={`button-${i}`}
          text={option}
          status={status}
          isImage={isImage}
          onPress={() => setSelectedIndex(i)}
          disabled={disabled}
          optionKey={`${i + 1}`}
        />
      );
    }

    return (
      <ChoiceBubble
        key={`button-${i}`}
        text={option}
        status={status}
        onPress={() => setSelectedIndex(i)}
        disabled={disabled}
        optionKey={`${i + 1}`}
        buttonStyle={styles.button}
      />
    );
  });

  return (
    <>
      <View style={[styles.container, containerStyle]}>{buttons}</View>
      <CheckButton isVisible={showCheck} onPress={verifyAnswer} />
    </>
  );
}
